// src/pages/healthcare/results/CreateTestOrderModal.tsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { useQuery } from "@tanstack/react-query";
import { useNotifications } from "../../../context/NotificationContext";
import { Button, Dropdown, Space, Tabs, Tooltip } from "antd";
import { SaveOutlined, PrinterOutlined, UploadOutlined, ReloadOutlined } from "@ant-design/icons";
import StatusTag from "../../../components/shared/StatusTag";
import { useAnalysisPolling } from "../../../hooks/useAnalysisPolling";

import DetailDrawer from "../../../components/shared/DetailDrawer";

import { fetchCustomTestPackages,  fetchCustomTestPackageFormConfig } from "../../../api/healthcare/Testing/CustomTestPackageApi";
import { createTest, fetchTest, updateTest, createTestWithResults } from "../../../api/healthcare/Testing/TestApi";
import { fetchTestCategories } from "../../../api/healthcare/Testing/TestCategories";
import { fetchTestTypesByCategory } from "../../../api/healthcare/Testing/TestTypes";
import { fetchBloodTestCatalogue } from "../../../api/healthcare/Testing/BloodTestCatalogueApi";

import TestOrderDetails from "./testOrderComponents/TestOrderDetails";
import DynamicFormFields from "./testOrderComponents/DynamicFormFields";
import TestResultsTab from "./testOrderComponents/TestResultsTab";
import InitialResultsUpload from "./testOrderComponents/InitialResultsUpload";
import PrintOrderModal from "./PrintOrderModal";
import ResultUploadModal from "../patient/modalComponents/submodals/ResultUploadModal";

import { Test, CustomTestPackage, TestCategory, FormField, TestType } from "../../../types/healthcare/TestTypes";
import { Patient } from "../../../types/healthcare/PatientTypes";

import { User } from "../../../types/admin/UserTypes";

// Clinical hint for a selectable blood test, resolved from the blood-test
// catalogue. Additive annotation only — not part of the selection payload.
export interface BloodTestHint {
	categoryName: string;
	description: string | null;
	clinicalUse: string | null;
	units: string | null;
}

interface CreateTestModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave?: (test: Test) => void;
	currentOrder: Partial<Test>;
	setCurrentOrder: React.Dispatch<React.SetStateAction<Partial<Test | null>>>;
	patient?: Patient;
	allTests?: Test[];
	allowedPackageIds?: number[];
}

const CreateTestModal: React.FC<CreateTestModalProps> = ({ isOpen, onClose, onSave, currentOrder, setCurrentOrder, patient, allTests, allowedPackageIds }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isNotLocked, setIsNotLocked] = useState<boolean>(true);
	const [activeTab, setActiveTab] = useState<string>("general");

	const [printModalOpen, setPrintModalOpen] = useState<boolean>(false);
	const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
	const [customPackages, setCustomPackages] = useState<CustomTestPackage[]>([]);
	const [formFields, setFormFields] = useState<FormField[]>([]);

	// Direct-mode state (no package selected — practitioner picks category + type directly)
	const [orderMode, setOrderMode] = useState<'package' | 'direct'>('package');
	const [availableTestCategories, setAvailableTestCategories] = useState<TestCategory[]>([]);
	const [availableTestTypes, setAvailableTestTypes] = useState<TestType[]>([]);

	// File upload state for initial results
	interface UploadedFile {
		file: File;
		name: string;
		size: number;
		type: string;
		preview?: string;
	}
	
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	const [resultNotes, setResultNotes] = useState<string>("");
	const [resultStatus, setResultStatus] = useState<string>("normal");

	const { showError, showSuccess } = useNotifications();

	const user: User = getSessionUser();

	// Blood-test catalogue — used purely to annotate the selectable blood tests with
	// their clinical description/use as hints. Additive overlay only: it does NOT
	// drive the TestCategory/TestType selection flow. Cached aggressively.
	const { data: bloodTestCatalogue } = useQuery({
		queryKey: ["blood-test-catalogue"],
		queryFn: fetchBloodTestCatalogue,
		staleTime: 1000 * 60 * 60, // 1h
		gcTime: 1000 * 60 * 60 * 24, // 24h
		retry: 1,
	});

	// Lookup from a test-type value (and any alias) → its clinical hint + sub-category.
	// Built once per catalogue load; consumed by TestOrderDetails to annotate the
	// existing test-type options without changing how they are selected.
	const bloodTestHints = useMemo(() => {
		const map: Record<string, BloodTestHint> = {};
		(bloodTestCatalogue || []).forEach((subCategory) => {
			(subCategory.test_types ?? []).forEach((type) => {
				const hint: BloodTestHint = {
					categoryName: subCategory.name,
					description: type.description,
					clinicalUse: type.clinical_use,
					units: type.units,
				};
				const keys = [type.value, ...(type.aliases || [])].filter(Boolean);
				keys.forEach((k) => {
					map[k.toLowerCase()] = hint;
				});
			});
		});
		return map;
	}, [bloodTestCatalogue]);

	// Default to the Results tab when the opened order already has results; otherwise
	// General Information. Reset on open / order change — the drawer keeps content
	// mounted, so a one-shot default key would not re-evaluate on reopen.
	useEffect(() => {
		if (isOpen) {
			setActiveTab((currentOrder.test_results?.length ?? 0) > 0 ? "results" : "general");
		}
	}, [isOpen, currentOrder.id]);

	const patientId = patient?.id;

	// load the compoentnts when modal opens
	useEffect(() => {
		if (!isOpen) return;

		if (currentOrder.id === 0 || currentOrder.id === undefined) {
			setCurrentOrder((prev) => ({
				...prev,
				patient_id: patientId || prev?.patient_id || "",
					ordering_provider_id: user.id,
				ordering_provider: {
					id: user.id,
					first_name: user.first_name,
					last_name: user.last_name,
				},
				priority: prev?.priority || "routine",
				status: prev?.status || "ordered",
				details: prev?.details || {},
				test_type: prev?.test_type || [],
				...(allowedPackageIds && allowedPackageIds.length > 0
					? { custom_test_package_ids: allowedPackageIds }
					: {}),
			}));
			// Reset ordering mode for new orders — force package mode when packages are restricted.
			setOrderMode('package');
			setAvailableTestCategories([]);
			setAvailableTestTypes([]);
			if (allowedPackageIds && allowedPackageIds.length > 0) {
				loadPackageConfig(allowedPackageIds[0]);
			}
		} else {
			let packageIds: number[] = [];
			for (const pkg of currentOrder.custom_test_packages || []) {
				packageIds.push(pkg.id);
			}
			setCurrentOrder((prev) => ({
				...prev,
				custom_test_package_ids: packageIds,
			}));
			// An existing order with NO package but a saved category/type was placed in direct
			// mode — switch to direct so its category/type renders instead of an empty package
			// selector (bug 86d3gy359). The category/type option lists are loaded below.
			const isExistingDirect =
				packageIds.length === 0 && (Boolean(currentOrder.test_category) || (currentOrder.test_type?.length ?? 0) > 0);
			setOrderMode(isExistingDirect ? 'direct' : 'package');
		}

		if (currentOrder?.custom_test_packages && currentOrder.custom_test_packages.length > 0) {
			// Load configuration for the first package (could be enhanced to merge configs)
			const primaryPackageId = currentOrder.custom_test_packages[0].id;
			loadPackageConfig(primaryPackageId);
		} else {
			setFormFields([]);
		}

		setIsNotLocked(Boolean(currentOrder?.status !== "completed"));
	}, [isOpen, showError]);

	const loadCustomPackages = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await fetchCustomTestPackages({
				active_only: true,
				include_analysis_types: true,
			});
			const filtered = allowedPackageIds && allowedPackageIds.length > 0
				? data.filter((p) => allowedPackageIds.includes(p.id))
				: data;
			setCustomPackages(filtered);
		} catch (error) {
			showError("Failed to load custom test packages");
		} finally {
			setIsLoading(false);
		}
	}, [allowedPackageIds, showError]);

	const loadPackageConfig = useCallback(
		async (packageId: number) => {
			try {
				const response = await fetchCustomTestPackageFormConfig(packageId);
				if (response.success) {
					const config = response.data;
					setFormFields(config.fields || []);
				}
			} catch (error) {
				showError("Failed to load package configuration");
			}
		},
		[showError],
	);

	useEffect(() => {
		if (allowedPackageIds && allowedPackageIds.length > 0) {
			loadPackageConfig(allowedPackageIds[0]);
		}
	}, []);

	const loadTestCategories = useCallback(async () => {
		try {
			const response = await fetchTestCategories({ include_test_types: false });
			if (response.success) {
				setAvailableTestCategories(response.data.filter((c: TestCategory) => c.is_active));
			}
		} catch (error) {
			showError("Failed to load test categories");
		}
	}, [showError]);

	const handleDirectCategoryChange = useCallback(async (categoryValue: string) => {

		setAvailableTestTypes([]);
		setCurrentOrder((prev) => ({ ...prev, test_category: categoryValue, test_type: [] }));

		try {
			const response = await fetchTestTypesByCategory(categoryValue);
			if (response.success) {
				setAvailableTestTypes(response.data.filter((t: TestType) => t.is_active));
			}
		} catch (error) {
			showError("Failed to load test types");
		}
	}, [showError, setCurrentOrder]);

	useEffect(() => {
		if (isOpen) {
			loadCustomPackages();
		}
	}, [isOpen, loadCustomPackages]);

	useEffect(() => {
		if (isOpen && orderMode === 'direct') {
			loadTestCategories();
		}
	}, [isOpen, orderMode, loadTestCategories]);

	// For an EXISTING direct order, load the test types of the saved category so the saved
	// test_type values render with labels. Unlike handleDirectCategoryChange (user-driven, which
	// resets test_type to []), this preserves the saved selection. Guarded on currentOrder.id so it
	// never runs for new orders — those stay driven by handleDirectCategoryChange. Bug 86d3gy359.
	useEffect(() => {
		if (!isOpen || !currentOrder.id || orderMode !== 'direct') return;
		const category = currentOrder.test_category;
		if (!category) return;

		let cancelled = false;
		(async () => {
			try {
				const response = await fetchTestTypesByCategory(category);
				if (!cancelled && response.success) {
					setAvailableTestTypes(response.data.filter((t: TestType) => t.is_active));
				}
			} catch (error) {
				if (!cancelled) showError("Failed to load test types");
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isOpen, currentOrder.id, orderMode, currentOrder.test_category, showError]);

	const handleFieldChange = useCallback(
		(field: string, value: any) => {
			setCurrentOrder((prev) => ({
				...prev,
				[field]: value,
			}));
		},
		[setCurrentOrder],
	);

	const handleDetailChange = useCallback(
		(fieldName: string, value: any) => {
			setCurrentOrder((prev) => ({
				...prev,
				details: {
					...(prev?.details || {}),
					[fieldName]: value,
				},
			}));
		},
		[setCurrentOrder],
	);

	const handlePackageChange = useCallback(
		(packageIds: number[]) => {
			// Combine test types from all selected packages
			const selectedPackages = customPackages.filter((pkg) => packageIds.includes(pkg.id));
			const combinedTestTypes = selectedPackages.flatMap((pkg) => pkg.analysis_types || []);
			const combinedUniqueTestTypes = [...new Set(combinedTestTypes)];

			// Use first package's category for compatibility (could be enhanced to combine categories)
			const primaryCategory = selectedPackages[0]?.analysis_type_details?.[0]?.test_category?.value.toString() || "";

			setCurrentOrder((prev) => ({
				...prev,
				custom_test_package_ids: packageIds,
				// Keep for backward compatibility
				custom_test_package_id: packageIds[0] || undefined,
				test_type: combinedUniqueTestTypes,
				test_category: primaryCategory,
				testing_provider_id: undefined,
				details: {},
			}));
			if (packageIds.length > 0) {
				loadPackageConfig(packageIds[0]);
			} else {
				setFormFields([]);
			}
		},
		[customPackages, setCurrentOrder],
	);

	const handlePrintOrder = useCallback(() => {
		if (currentOrder.id) {
			setPrintModalOpen(true);
		} else {
			showError("Please save the order before printing");
		}
	}, [currentOrder.id, showError]);

	const printMenuItems = [
		{
			key: "print",
			label: "Print Order",
			icon: <PrinterOutlined />,
			onClick: handlePrintOrder,
			disabled: !currentOrder.id,
		},
		{
			key: "save-print",
			label: "Save and Print",
			icon: <SaveOutlined />,
			onClick: async () => {
				await handleSave();
				if (currentOrder.id) {
					setPrintModalOpen(true);
				}
			},
		},
	];

	const handleSave = useCallback(async () => {
		if (orderMode === 'package' && (!currentOrder.custom_test_package_ids || currentOrder.custom_test_package_ids.length === 0)) {
			showError("Please select a test package");
			return;
		}

		if (orderMode === 'direct' && (!currentOrder.test_category || !currentOrder.test_type || currentOrder.test_type.length === 0)) {
			showError("Please select a test category and at least one test type");
			return;
		}

		if (!currentOrder.patient_id) {
			showError("Patient is required");
			return;
		}

		setIsSubmitting(true);
		try {
			let result;
			let orderToSave = {
				...currentOrder,
			};
			orderToSave.details = JSON.stringify(orderToSave.details || {});

			if (currentOrder.id) {
				// Updating existing order
				result = await updateTest(currentOrder.id, orderToSave);
				showSuccess("Test order updated successfully");
			} else {
				// Creating new order
				if (uploadedFiles.length > 0) {
					// Create order with initial results using FormData
					const formData = new FormData();

					// Add test order fields
					formData.append("patient_id", orderToSave.patient_id!.toString());
					formData.append("test_category", orderToSave.test_category || "");
					formData.append("test_name", orderToSave.test_name || "");
					formData.append("priority", orderToSave.priority || "routine");
					// Uploading a document does NOT mean results are ready — the analysis runs
					// asynchronously afterwards. The order stays "In Progress" until it lands
					// (the server overrides this on analysis paths anyway; sending the truthful
					// value keeps the payload honest). `status` is validated as required.
					formData.append("status", "in_progress");

					// Add custom_test_package_ids as array
					orderToSave.custom_test_package_ids?.forEach((packageId: number) => {
						formData.append("custom_test_package_ids[]", packageId.toString());
					});

					// Add test_type array (from selected packages)
					if (Array.isArray(orderToSave.test_type)) {
						orderToSave.test_type.forEach((type: string) => {
							formData.append("test_type[]", type);
						});
					} else if (orderToSave.test_type) {
						formData.append("test_type", orderToSave.test_type);
					}

					// Optional fields
					if (orderToSave.ordering_provider_id) {
						formData.append("ordering_provider_id", orderToSave.ordering_provider_id.toString());
					}
					if (orderToSave.facility) {
						formData.append("facility", orderToSave.facility);
					}
					if (orderToSave.sample_id) {
						formData.append("sample_id", orderToSave.sample_id);
					}
					if (orderToSave.sample_type) {
						formData.append("sample_type", orderToSave.sample_type);
					}
					if (orderToSave.test_date) {
						formData.append("test_date", typeof orderToSave.test_date === "string" ? orderToSave.test_date : new Date(orderToSave.test_date).toISOString());
					}
					if (orderToSave.reason) {
						formData.append("reason", orderToSave.reason);
					}
					if (orderToSave.billable_diagnosis) {
						formData.append("billable_diagnosis", orderToSave.billable_diagnosis);
					}
					if (orderToSave.details) {
						formData.append("details", orderToSave.details);
					}

					// Add result fields with underscore prefix (PHP converts dots to underscores)
					formData.append("result_result_notes", resultNotes);
					formData.append("result_is_abnormal", resultStatus === "abnormal" || resultStatus === "critical" ? "1" : "0");
					formData.append("result_requires_followup", resultStatus === "critical" || resultStatus === "pending_review" ? "1" : "0");
					formData.append("result_perform_analysis", "1");
					formData.append("result_uploaded_by", user.id.toString());
					formData.append("result_status", "in_progress");

					// Add files metadata as JSON
					formData.append(
						"result_results",
						JSON.stringify(
							uploadedFiles.map((file) => ({
								name: file.name,
								size: file.size,
								type: file.type,
							})),
						),
					);

					// Add uploaded files
					uploadedFiles.forEach((uploadedFile) => {
						formData.append("result_files[]", uploadedFile.file);
					});

					const response = await createTestWithResults(formData);
					result = response.test;

					// The collection count is not knowable here — it arrives with the practitioner's
					// notification once BloodTestAnalysisJob has read the report.
					showSuccess(`Test order created successfully — analysis is in progress. You will be notified when it finishes, including any earlier collections found in the same report.`);
				} else {
					// Create order without results
					result = await createTest(orderToSave);

					showSuccess("Test order created successfully");
				}

				setCurrentOrder(result);
			}

			if (onSave) {
				onSave(result);
			}
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to save test order");
		} finally {
			setIsSubmitting(false);
		}
	}, [currentOrder, orderMode, setCurrentOrder, onSave, showError, showSuccess, uploadedFiles, resultNotes, resultStatus, user]);

	// Refresh the order after an inline result review is saved
	const handleResultSaved = useCallback(async () => {
		if (currentOrder.id) {
			try {
				const updated = await fetchTest(currentOrder.id);
				setCurrentOrder(updated);
				if (onSave) onSave(updated);
			} catch {}
		}
	}, [currentOrder.id, onSave, setCurrentOrder]);

	// 86d3vhb3j S6 — an order created from an uploaded document stays `in_progress`
	// until its analysis pipeline finishes (minutes, not seconds). Re-fetch it on a slow
	// interval so an open drawer flips to Results Available / Analysis Failed in place.
	//
	// Deliberately NOT handleResultSaved: that calls onSave, and some callers read that
	// as "the practitioner saved" — TestingTab toasts and closes the drawer on it.
	// `analysis_failed` is terminal, so it is not polled; it renders S5's retry state.
	const refreshOrderQuietly = useCallback(async () => {
		if (!currentOrder.id) return;
		try {
			const updated = await fetchTest(currentOrder.id);
			// Merged, not replaced: callers attach client-side fields to the order (TestingTab
			// injects `patient`) that the API response does not carry back.
			setCurrentOrder((prev) => ({ ...prev, ...updated }));
		} catch {
			// A background refresh must stay silent — the next tick retries.
		}
	}, [currentOrder.id, setCurrentOrder]);

	const { exhausted: analysisPollExhausted } = useAnalysisPolling({
		active: isOpen && Boolean(currentOrder.id) && currentOrder.status === "in_progress",
		onPoll: refreshOrderQuietly,
	});

	const handleUploadClose = useCallback(async () => {
		setUploadModalOpen(false);
		if (currentOrder.id) {
			try {
				const updated = await fetchTest(currentOrder.id);
				setCurrentOrder(updated);
				if (onSave) onSave(updated);
			} catch {}
		}
	}, [currentOrder.id, onSave, setCurrentOrder]);

	const anyAbnormal = currentOrder.test_results?.some((r) => r.is_abnormal);
	const anyFollowup = currentOrder.test_results?.some((r) => r.requires_followup);

	const drawerSubtitle = currentOrder.id
		? [currentOrder.custom_test_packages?.map((p) => p.name).join(", "), currentOrder.sample_id].filter(Boolean).join(" · ")
		: patient
		? `Patient: ${patient.first_name} ${patient.last_name}`
		: undefined;

	const drawerTitleMeta = currentOrder.id ? (
		<span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
			{currentOrder.status && <StatusTag status={currentOrder.status} size="small" />}
			{anyAbnormal && <StatusTag status="failed" label="Abnormal" size="small" />}
			{anyFollowup && <StatusTag status="waiting" label="Follow-up" size="small" />}
		</span>
	) : undefined;

	return (
		<>
			<DetailDrawer
				open={isOpen}
				title={currentOrder.id ? "Test Order Details" : "Create Test Order"}
				subtitle={drawerSubtitle}
				titleMeta={drawerTitleMeta}
				width="85vw"
				onClose={onClose}
				headerActions={
					<Space>
						{analysisPollExhausted && (
							<Tooltip title="This order has been analysing for a while, so auto-refresh has stopped. Check again manually.">
								<Button icon={<ReloadOutlined />} onClick={refreshOrderQuietly}>
									Refresh
								</Button>
							</Tooltip>
						)}
						{currentOrder.id && (
							<>
								<Button icon={<UploadOutlined />} onClick={() => setUploadModalOpen(true)}>
									Upload Results
								</Button>
								<Dropdown menu={{ items: printMenuItems }} trigger={["click"]}>
									<Button icon={<PrinterOutlined />}>Print</Button>
								</Dropdown>
							</>
						)}
						<Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={isSubmitting} disabled={isSubmitting || isLoading}>
							{currentOrder.id ? "Update Order" : "Create Order"}
						</Button>
					</Space>
				}
			>
				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					size="small"
					items={[
						{
							key: "general",
							label: "General Information",
							children: (
								<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
									<TestOrderDetails
										currentOrder={currentOrder}
										customPackages={customPackages}
										orderMode={orderMode}
										onOrderModeChange={setOrderMode}
										availableTestCategories={availableTestCategories}
										availableTestTypes={availableTestTypes}
										bloodTestHints={bloodTestHints}
										onDirectCategoryChange={handleDirectCategoryChange}
										isNotLocked={isNotLocked}
										isLoading={isLoading}
										handleFieldChange={handleFieldChange}
										handlePackageChange={handlePackageChange}
									/>

									<DynamicFormFields currentOrder={currentOrder} formFields={formFields} isNotLocked={isNotLocked} handleDetailChange={handleDetailChange} />

									{!currentOrder.id && (
										<InitialResultsUpload
											uploadedFiles={uploadedFiles}
											resultNotes={resultNotes}
											resultStatus={resultStatus}
											onFilesChange={setUploadedFiles}
											onNotesChange={setResultNotes}
											onStatusChange={setResultStatus}
											disabled={!isNotLocked || isLoading}
										/>
									)}
								</div>
							),
						},
						...(currentOrder.id &&
						((currentOrder.test_results?.length ?? 0) > 0 ||
							currentOrder.status === "in_progress" ||
							currentOrder.status === "analysis_failed")
							? [
									{
										key: "results",
										label: <span data-testid="results-results-tab">Results</span>,
										children: <TestResultsTab currentOrder={currentOrder} patient={patient} allTests={allTests} onResultSaved={handleResultSaved} />,
									},
								]
							: []),
					]}
				/>
			</DetailDrawer>
			{printModalOpen && currentOrder.id && <PrintOrderModal isOpen={printModalOpen} onClose={() => setPrintModalOpen(false)} orderIds={[currentOrder.id as number]} title={`Print Test Order #${currentOrder.id}`} />}
			{uploadModalOpen && currentOrder.id && <ResultUploadModal isOpen={uploadModalOpen} onClose={handleUploadClose} test={currentOrder as Test} />}
		</>
	);
};

export default CreateTestModal;
